import Typography from '@mui/material/Typography';

import { DateFormat, formatDateTime, getRelativeTime } from 'src/compass/shared/util/casMoment';

/*
  How far the HubSpot poll has got, from hubspot.Properties - over Poll Now on Administration >
  Integrations > Hubspot > Properties, and over the Hubspot Sync tabs:
  - when it last ran (ContactPollLastRun) - its timer or Poll Now, changes or not. With Contact Poll
    Seconds on, a time much older than the interval means the timer has stopped - or, on a local
    backend, never started (HUBSPOT_POLL_DISABLED).
  - how far it has got (ContactPollWatermark): the time of the newest HubSpot change it has applied.
    A poll that finds nothing newer leaves it where it is, so on a quiet day it can be hours old
    while the poll is healthy.
  - the same for companies (CompanyPollWatermark) and deals (DealPollWatermark). They change less
    often than contacts, so they are usually older. Each is missing while HubSpot has not let the app
    read them.
*/
export default function HubspotPollStatus({
  lastRun,
  watermark,
  companyWatermark,
  dealWatermark,
}: {
  lastRun?: string | null;
  watermark?: string | null;
  companyWatermark?: string | null;
  dealWatermark?: string | null;
}) {
  if (!lastRun && !watermark) {
    return (
      <Typography variant="body2" sx={{ px: 2.5, pb: 1 }}>
        The contact poll has not run on this database yet.
      </Typography>
    );
  }

  return (
    <Typography component="div" variant="body2" sx={{ px: 2.5, pb: 1 }}>
      {lastRun && (
        <div>
          Contact poll last ran <b>{formatDateTime(lastRun, DateFormat.MDY_HMS)}</b> (
          {getRelativeTime(lastRun)}) - by its timer or Poll Now.
        </div>
      )}
      {watermark && (
        <div>
          Contacts polled through <b>{formatDateTime(watermark, DateFormat.MDY_HMS)}</b> - the
          newest change in HubSpot that the poll has applied, made {getRelativeTime(watermark)}. A
          poll that finds nothing newer leaves it unchanged.
        </div>
      )}
      {companyWatermark && (
        <div>
          Companies polled through <b>{formatDateTime(companyWatermark, DateFormat.MDY_HMS)}</b> -
          made {getRelativeTime(companyWatermark)}. Every company&apos;s contacts are read on each
          pass.
        </div>
      )}
      {dealWatermark && (
        <div>
          Deals polled through <b>{formatDateTime(dealWatermark, DateFormat.MDY_HMS)}</b> - made{' '}
          {getRelativeTime(dealWatermark)}.
        </div>
      )}
    </Typography>
  );
}
